/*───────────────────────────────────────────────────────────────
 📄 File: components/webrtc/WebRTCActiveCallScreen.tsx
 🧩 Description:
    In-call screen: call info, held call actions, transfer dialog,
    DTMF keypad, call controls (mute/hold/transfer/keypad),
    and hangup button.
    Extracted from WebRTCPhonePanel.tsx.
────────────────────────────────────────────────────────────────*/

import { Phone, PhoneOff, Pause, Play, MicOff, Mic, ArrowRightLeft, X } from 'lucide-react';
import { CallState } from '../../hooks/useSipPhone';

const DIAL_PAD = [['1','2','3'],['4','5','6'],['7','8','9'],['*','0','#']];

interface CallInfo { remoteName?: string | null; remoteNumber?: string | null; startTime?: number | null; }

interface Props {
    callState: CallState;
    callInfo: CallInfo | null;
    heldCallInfo: CallInfo | null;
    isMuted: boolean;
    isOnHold: boolean;
    hasHeldCall: boolean;
    callDuration: number;
    showTransfer: boolean;
    showKeypad: boolean;
    transferNumber: string;
    onTransferNumberChange: (v: string) => void;
    onSetShowTransfer: (v: boolean) => void;
    onSetShowKeypad: (v: boolean) => void;
    onToggleMute: () => void;
    onHold: () => void;
    onUnhold: () => void;
    onTransfer: () => void;
    onAttendedTransfer: () => void;
    onSendDTMF: (d: string) => void;
    onHangup: () => void;
    formatDuration: (s: number) => string;
    getCallStateText: (s: CallState) => string;
}

export function WebRTCActiveCallScreen({
    callState, callInfo, heldCallInfo, isMuted, isOnHold, hasHeldCall,
    callDuration, showTransfer, showKeypad, transferNumber,
    onTransferNumberChange, onSetShowTransfer, onSetShowKeypad,
    onToggleMute, onHold, onUnhold, onTransfer, onAttendedTransfer,
    onSendDTMF, onHangup, formatDuration, getCallStateText,
}: Props) {
    return (
        <div className="flex-1 flex flex-col">
            {/* Call Info */}
            <div className="bg-muted/50 rounded-lg p-4 mb-3 text-center">
                <div className="text-xl font-semibold text-foreground mb-1">
                    {callInfo?.remoteName || callInfo?.remoteNumber || 'Unknown'}
                </div>
                {callInfo?.remoteName && callInfo?.remoteNumber && (
                    <div className="text-sm text-muted-foreground mb-2">{callInfo.remoteNumber}</div>
                )}
                <div className="text-lg text-primary font-medium">
                    {callState === 'active' ? formatDuration(callDuration) : getCallStateText(callState)}
                </div>
            </div>

            {/* Held Call */}
            {hasHeldCall && callState === 'active' && (
                <div className="mb-3">
                    <div className="text-xs text-yellow-400 flex items-center justify-center gap-1 mb-2">
                        <Pause className="w-3 h-3" />
                        <span>On Hold: {heldCallInfo?.remoteName || heldCallInfo?.remoteNumber}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={onAttendedTransfer} className="p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm">
                            <ArrowRightLeft className="w-4 h-4" /> Transfer
                        </button>
                        <button onClick={onUnhold} className="p-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm">
                            <Play className="w-4 h-4" /> Resume
                        </button>
                    </div>
                </div>
            )}

            {/* Transfer Dialog */}
            {showTransfer && (
                <div className="mb-3 p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm font-medium mb-2">Transfer to:</div>
                    <div className="flex gap-2">
                        <input type="tel" value={transferNumber} onChange={(e) => onTransferNumberChange(e.target.value)} placeholder="Extension or number" className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm" autoFocus />
                        <button onClick={onTransfer} disabled={!transferNumber} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-sm">Transfer</button>
                        <button onClick={() => onSetShowTransfer(false)} className="px-2 py-2 bg-muted hover:bg-muted/80 rounded-md"><X className="w-4 h-4" /></button>
                    </div>
                </div>
            )}

            {/* DTMF Keypad */}
            {showKeypad && (
                <div className="mb-3 grid grid-cols-3 gap-1">
                    {DIAL_PAD.map((row, ri) => row.map((d, ci) => (
                        <button key={`dtmf-${ri}-${ci}`} onClick={() => onSendDTMF(d)} className="py-2 text-base font-medium bg-muted/50 hover:bg-muted text-foreground rounded transition-colors">{d}</button>
                    )))}
                </div>
            )}

            {/* Call Controls */}
            <div className="grid grid-cols-4 gap-2 mb-3 mt-auto">
                <button onClick={onToggleMute} className={`p-3 rounded-lg transition-colors flex flex-col items-center justify-center gap-1 ${isMuted ? 'bg-red-600/30 text-red-400' : 'bg-muted hover:bg-muted/80 text-foreground'}`}>
                    {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    <span className="text-[10px]">{isMuted ? 'Unmute' : 'Mute'}</span>
                </button>
                <button onClick={isOnHold ? onUnhold : onHold} className={`p-3 rounded-lg transition-colors flex flex-col items-center justify-center gap-1 ${isOnHold ? 'bg-yellow-600/30 text-yellow-400' : 'bg-muted hover:bg-muted/80 text-foreground'}`}>
                    {isOnHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                    <span className="text-[10px]">{isOnHold ? 'Resume' : 'Hold'}</span>
                </button>
                <button onClick={() => onSetShowTransfer(true)} className="p-3 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors flex flex-col items-center justify-center gap-1">
                    <ArrowRightLeft className="w-5 h-5" />
                    <span className="text-[10px]">Transfer</span>
                </button>
                <button onClick={() => onSetShowKeypad(!showKeypad)} className={`p-3 rounded-lg transition-colors flex flex-col items-center justify-center gap-1 ${showKeypad ? 'bg-primary/30 text-primary' : 'bg-muted hover:bg-muted/80 text-foreground'}`}>
                    <Phone className="w-5 h-5" />
                    <span className="text-[10px]">Keypad</span>
                </button>
            </div>

            {/* Hangup */}
            <button onClick={onHangup} className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium">
                <PhoneOff className="w-5 h-5" /> End Call
            </button>
        </div>
    );
}
